import * as React from "react"

/**
 * TUT-472 — devolve o foco a QUEM ABRIU o diálogo, e não ao `<Dialog.Trigger>`.
 *
 * O Radix Dialog modal, ao fechar, faz `preventDefault` no
 * `onCloseAutoFocus` e foca `context.triggerRef.current` (react-dialog
 * 1.1.23, `DialogContentModal`). Quase todo diálogo da casa abre por ESTADO
 * (`open={aberto}`, ou o pai monta e desmonta o `DialogoDaCasa`), sem
 * Trigger: o ref é nulo, o `preventDefault` impede o `FocusScope` de devolver
 * o foco ao elemento anterior, e o foco caía no `<body>` — o Tab seguinte
 * recomeçava em "Pular para o conteúdo". O QA mediu isso em ~40 diálogos.
 *
 * No `onOpenAutoFocus` o foco ainda está em quem abriu (o `FocusScope` dispara
 * o evento ANTES de mover o foco); guardamos esse elemento e o devolvemos no
 * fechamento, se ele ainda estiver no DOM. Sem origem viva, `reserva` (o
 * gatilho do topo, na gaveta móvel do menu) e, sem ela, o padrão do Radix.
 * O handler de quem chama roda antes e pode vetar com `preventDefault`.
 */
export function useDevolverFoco({
  onOpenAutoFocus,
  onCloseAutoFocus,
  reserva,
}: {
  onOpenAutoFocus?: ((event: Event) => void) | undefined
  onCloseAutoFocus?: ((event: Event) => void) | undefined
  reserva?: () => HTMLElement | null
} = {}) {
  const origem = React.useRef<HTMLElement | null>(null)
  return {
    onOpenAutoFocus: (event: Event) => {
      const ativo = typeof document === "undefined" ? null : document.activeElement
      // A origem é sempre a DESTA abertura. Reaproveitar a da anterior quando
      // a reabertura parte do `<body>` mandava o foco, nos diálogos que ficam
      // montados entre aberturas (Agenda do dia, gaveta), para um botão
      // antigo — e no Safari, que não foca botão no clique, isso é toda
      // abertura por mouse. Sem origem, vale o padrão do Radix.
      origem.current = ativo instanceof HTMLElement && ativo !== document.body ? ativo : null
      onOpenAutoFocus?.(event)
    },
    onCloseAutoFocus: (event: Event) => {
      onCloseAutoFocus?.(event)
      if (event.defaultPrevented) return
      const anterior = origem.current
      const alvo = anterior?.isConnected ? anterior : (reserva?.() ?? null)
      if (!alvo) return
      event.preventDefault()
      alvo.focus()
    },
  }
}
